import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { shiftsService, type ShiftMemberResponse, type AddMemberRequest } from '../services/shiftsService';

/**
 * Hook para obter membros de um turno
 * @param shiftId ID do turno
 * @param role Filtrar por role (opcional): 'primary' ou 'backup'
 */
export const useShiftMembers = (
  shiftId: string | undefined,
  role?: 'primary' | 'backup'
) => {
  return useQuery<ShiftMemberResponse[]>({
    queryKey: ['shift-members', shiftId, role],
    queryFn: () => shiftsService.getShiftMembers(shiftId!, role),
    enabled: !!shiftId,
  });
};

/**
 * Hook para adicionar membro a um turno
 */
export const useAddShiftMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ shiftId, data }: { shiftId: string; data: AddMemberRequest }) =>
      shiftsService.addMember(shiftId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['shift-members', variables.shiftId] });
      queryClient.invalidateQueries({ queryKey: ['shift', variables.shiftId] });
    },
  });
};

/**
 * Hook para remover membro de um turno
 */
export const useRemoveShiftMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ shiftId, userId }: { shiftId: string; userId: string }) =>
      shiftsService.removeMember(shiftId, userId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['shift-members', variables.shiftId] });
      queryClient.invalidateQueries({ queryKey: ['shift', variables.shiftId] });
    },
  });
};

/**
 * Hook para atualizar role de membro em um turno
 */
export const useUpdateShiftMemberRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ shiftId, userId, role }: { shiftId: string; userId: string; role: 'primary' | 'backup' }) =>
      shiftsService.updateMemberRole(shiftId, userId, role),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['shift-members', variables.shiftId] });
      queryClient.invalidateQueries({ queryKey: ['shift', variables.shiftId] });
    },
  });
};
